/**
 * Search Console → PageMind-Improved Pages section.
 *
 * Customer-facing view of the GSC impact of PageMind-generated AEO content,
 * scoped to the specific pages we improved (cohort = pmr ∩ apg.published).
 * Before/after windows are symmetric. By default anchored on each page's
 * own deployment date (publisher_manifest_records.first_seen_at); the user
 * can override with a single calendar date applied to every page (v1.1).
 * Window: 7|14|28|60|90|120 days (v1.1: extended).
 *
 * task-kgf30wsu48 (orchestrator dispatch 2026-05-26).
 * v1.1 — Jaume review pass 2026-05-26: ref-date column truncation,
 * extended window menu (60/90/120d), calendar ref-date override.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  googleIntelligenceService,
  type GSCImprovedPage,
  type GSCImprovedCohortTotals,
  type GSCImprovedSortBy,
  type GSCImprovedWindowDays,
} from '@/services/googleIntelligenceService';

interface Props {
  siteId: string;
}

const PAGE_SIZE = 100;

// Display-only formatter: backend returns Postgres `date` which the pg
// driver serializes as 'YYYY-MM-DDT00:00:00.000Z'. We only want the date.
// (v1.1 — Jaume review: full ISO was too verbose in the table.)
function formatRefDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

// Pass the trimmed user input through to the backend (which now does strict
// real-calendar validation and returns 400 with the parse reason for
// malformed input). Empty string ⇒ undefined ⇒ "no override".
//
// We deliberately do NOT silently drop shape-violating input client-side —
// that would re-introduce the silent-fallback pattern the backend just
// removed in codex iter-1. (codex iter-2 PR #1396 minor 1.)
function refDateForRequest(s: string): string | undefined {
  const t = s.trim();
  return t === '' ? undefined : t;
}

// For UI-side bookkeeping only — whether a non-empty input *looks* like a
// well-formed YYYY-MM-DD. Doesn't validate the calendar (the backend does).
// Used to show the override label in the description / CSV filename only
// when the value is shape-plausible; otherwise the request fires, backend
// 400s, and the error appears in the red banner.
function looksLikeIsoDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(n));
}

function formatDelta(n: number, opts: { positiveIsGood?: boolean } = {}): JSX.Element {
  const positiveIsGood = opts.positiveIsGood ?? true;
  const isPositive = n > 0;
  const isNegative = n < 0;
  const good = (positiveIsGood && isPositive) || (!positiveIsGood && isNegative);
  const bad = (positiveIsGood && isNegative) || (!positiveIsGood && isPositive);
  const color = good ? 'text-emerald-600' : bad ? 'text-red-600' : 'text-muted-foreground';
  const sign = isPositive ? '+' : '';
  return <span className={color}>{sign}{formatNumber(n)}</span>;
}

function formatPct(n: number | null, digits = 2): string {
  if (n === null || Number.isNaN(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

function formatPctDelta(n: number | null): JSX.Element {
  if (n === null) return <span className="text-muted-foreground">—</span>;
  const color = n > 0 ? 'text-emerald-600' : n < 0 ? 'text-red-600' : 'text-muted-foreground';
  const sign = n > 0 ? '+' : '';
  return <span className={color}>{sign}{n.toFixed(2)} pp</span>;
}

function formatPosition(n: number | null): string {
  if (n === null || Number.isNaN(n)) return '—';
  return n.toFixed(1);
}

function formatPositionDelta(n: number | null): JSX.Element {
  if (n === null) return <span className="text-muted-foreground">—</span>;
  // Lower position is better — invert color semantics
  const color = n < 0 ? 'text-emerald-600' : n > 0 ? 'text-red-600' : 'text-muted-foreground';
  const sign = n > 0 ? '+' : '';
  return <span className={color}>{sign}{n.toFixed(2)}</span>;
}

function pathnameOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function toCsv(rows: GSCImprovedPage[]): string {
  const headers = [
    'page_url', 'reference_date',
    'clicks_before', 'clicks_after', 'clicks_delta',
    'impressions_before', 'impressions_after', 'impressions_delta',
    'ctr_before_pct', 'ctr_after_pct', 'ctr_delta_pp',
    'position_before', 'position_after', 'position_delta',
  ];
  const escape = (v: string | number | null) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    headers.join(','),
    ...rows.map(r => [
      r.pageUrl, r.referenceDate,
      r.clicksBefore, r.clicksAfter, r.clicksDelta,
      r.impressionsBefore, r.impressionsAfter, r.impressionsDelta,
      r.ctrBeforePct, r.ctrAfterPct, r.ctrDeltaPp,
      r.positionBefore, r.positionAfter, r.positionDelta,
    ].map(escape).join(',')),
  ];
  return lines.join('\n');
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function ImprovedPagesSection({ siteId }: Props) {
  const [windowDays, setWindowDays] = useState<GSCImprovedWindowDays>(28);
  // v1.1: optional reference-date override. Empty string = "use each page's
  // own deployment date" (per-page anchor, the default behavior).
  const [referenceDate, setReferenceDate] = useState<string>('');
  const [sortBy, setSortBy] = useState<GSCImprovedSortBy>('clicks_delta');
  const [urlSearchInput, setUrlSearchInput] = useState('');
  const [urlSearch, setUrlSearch] = useState('');     // debounced
  const [pages, setPages] = useState<GSCImprovedPage[]>([]);
  const [cohort, setCohort] = useState<GSCImprovedCohortTotals | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Debounce URL search input
  useEffect(() => {
    const t = setTimeout(() => {
      setUrlSearch(urlSearchInput.trim());
      setOffset(0);
    }, 350);
    return () => clearTimeout(t);
  }, [urlSearchInput]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [windowDays, sortBy, referenceDate]);

  const fetchData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await googleIntelligenceService.getGSCImprovedPages(siteId, {
        windowDays,
        referenceDate: refDateForRequest(referenceDate),
        urlSearch: urlSearch || undefined,
        sortBy,
        limit: PAGE_SIZE,
        offset,
      });
      setPages(result.pages);
      setCohort(result.cohort);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setPages([]);
      setCohort(null);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [siteId, windowDays, referenceDate, urlSearch, sortBy, offset]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  const handleExportCsv = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      // Page through the full filtered cohort until exhausted (cap each
      // page at the backend's 1000-row hard limit; loop on offset). Avoids
      // the silent-truncation pattern where a single 1000-cap request
      // produces an incomplete CSV the user can't distinguish from a
      // complete export. (codex iter-2.)
      const BATCH = 1000;
      const accumulated: GSCImprovedPage[] = [];
      let nextOffset = 0;
      // Hard ceiling so a broken `total` field can't infinite-loop the UI.
      // Sites have ~thousands of improved pages today; 100k is comfortable.
      const HARD_CEILING = 100_000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const batch = await googleIntelligenceService.getGSCImprovedPages(siteId, {
          windowDays,
          referenceDate: refDateForRequest(referenceDate),
          urlSearch: urlSearch || undefined,
          sortBy,
          limit: BATCH, offset: nextOffset,
        });
        accumulated.push(...batch.pages);
        if (batch.pages.length < BATCH || accumulated.length >= batch.total) break;
        if (accumulated.length >= HARD_CEILING) {
          throw new Error(
            `Export aborted at ${HARD_CEILING.toLocaleString()} rows (total reported ${batch.total.toLocaleString()}). ` +
            `Narrow the URL filter and try again.`
          );
        }
        nextOffset += BATCH;
      }
      const csv = toCsv(accumulated);
      const stamp = new Date().toISOString().slice(0, 10);
      const refForName = looksLikeIsoDate(referenceDate.trim()) ? referenceDate.trim() : '';
      const refSuffix = refForName ? `-ref-${refForName}` : '';
      downloadCsv(`pagemind-improved-pages-${windowDays}d${refSuffix}-${stamp}.csv`, csv);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to export');
    } finally {
      setLoading(false);
    }
  }, [siteId, windowDays, referenceDate, urlSearch, sortBy]);

  const cohortSummary = useMemo(() => {
    if (!cohort) return null;
    const ctrDeltaPp = (cohort.ctrAfterPct !== null && cohort.ctrBeforePct !== null)
      ? cohort.ctrAfterPct - cohort.ctrBeforePct
      : null;
    return { ...cohort, ctrDeltaPp };
  }, [cohort]);

  const pageStart = offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <Card>
      <CardHeader>
        <CardTitle>PageMind-Improved Pages</CardTitle>
        <CardDescription>
          Per-page GSC performance before vs after PageMind content deployment.
          Cohort = pages PageMind generated AEO content for AND that appear in
          the publisher manifest (i.e., actually went live). ±{windowDays}-day
          windows around{' '}
          {looksLikeIsoDate(referenceDate.trim())
            ? <strong>{referenceDate.trim()}</strong>
            : <span>each page's deployment date</span>
          }.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Cohort summary */}
        {cohortSummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 rounded-lg border bg-muted/30 p-4">
            <div>
              <div className="text-xs text-muted-foreground">Improved pages</div>
              <div className="text-xl font-semibold">{formatNumber(cohortSummary.totalPages)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Clicks {windowDays}d after vs before</div>
              <div className="text-xl font-semibold">
                {formatNumber(cohortSummary.clicksBefore)} → {formatNumber(cohortSummary.clicksAfter)}
                {' '}{formatDelta(cohortSummary.clicksDelta)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Impressions {windowDays}d after vs before</div>
              <div className="text-xl font-semibold">
                {formatNumber(cohortSummary.impressionsBefore)} → {formatNumber(cohortSummary.impressionsAfter)}
                {' '}{formatDelta(cohortSummary.impressionsDelta)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">CTR {windowDays}d after vs before</div>
              <div className="text-xl font-semibold">
                {formatPct(cohortSummary.ctrBeforePct)} → {formatPct(cohortSummary.ctrAfterPct)}
                {' '}{formatPctDelta(cohortSummary.ctrDeltaPp)}
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(parseInt(v, 10) as GSCImprovedWindowDays)}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7-day window</SelectItem>
              <SelectItem value="14">14-day window</SelectItem>
              <SelectItem value="28">28-day window</SelectItem>
              <SelectItem value="60">60-day window</SelectItem>
              <SelectItem value="90">90-day window</SelectItem>
              <SelectItem value="120">120-day window</SelectItem>
            </SelectContent>
          </Select>

          {/* v1.1: optional reference-date override. Empty = per-page deployment date. */}
          <div className="flex items-center gap-1">
            <Input
              type="date"
              value={referenceDate}
              onChange={(e) => setReferenceDate(e.target.value)}
              className="w-[170px]"
              title="Anchor the ±N-day window on this calendar date (instead of each page's deployment date)"
              aria-label="Reference date override"
            />
            {referenceDate && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setReferenceDate('')}
                title="Clear override — use each page's deployment date"
              >×</Button>
            )}
          </div>

          <Select value={sortBy} onValueChange={(v) => setSortBy(v as GSCImprovedSortBy)}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="clicks_delta">Sort: clicks delta</SelectItem>
              <SelectItem value="impressions_delta">Sort: impressions delta</SelectItem>
              <SelectItem value="ctr_delta_pp">Sort: CTR delta</SelectItem>
              <SelectItem value="position_delta">Sort: position improvement</SelectItem>
              <SelectItem value="clicks_after">Sort: clicks after</SelectItem>
              <SelectItem value="impressions_after">Sort: impressions after</SelectItem>
            </SelectContent>
          </Select>

          <Input
            placeholder="Filter URL..."
            value={urlSearchInput}
            onChange={(e) => setUrlSearchInput(e.target.value)}
            className="w-[260px]"
          />

          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {total > 0 ? `${formatNumber(pageStart)}–${formatNumber(pageEnd)} of ${formatNumber(total)}` : ''}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || loading}
            >Prev</Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total || loading}
            >Next</Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void handleExportCsv()}
              disabled={loading || total === 0}
            >Export CSV</Button>
          </div>
        </div>

        {/* Table */}
        {error && <div className="text-sm text-red-600">Error: {error}</div>}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[260px]">Page</TableHead>
                <TableHead className="text-right whitespace-nowrap">Deployed</TableHead>
                <TableHead className="text-right">Clicks (before → after)</TableHead>
                <TableHead className="text-right">Δ clicks</TableHead>
                <TableHead className="text-right">Imps (before → after)</TableHead>
                <TableHead className="text-right">Δ imps</TableHead>
                <TableHead className="text-right">CTR (before → after)</TableHead>
                <TableHead className="text-right">Δ CTR</TableHead>
                <TableHead className="text-right">Pos (before → after)</TableHead>
                <TableHead className="text-right">Δ pos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                    No PageMind-improved pages match the current filters. Once content is deployed
                    and GSC data accrues, results appear here.
                  </TableCell>
                </TableRow>
              ) : (
                pages.map((p) => (
                  <TableRow key={p.pageUrl}>
                    <TableCell
                      className="max-w-[400px] truncate"
                      title={p.pageUrl}
                    >
                      <a
                        href={p.pageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {pathnameOf(p.pageUrl)}
                      </a>
                    </TableCell>
                    <TableCell
                      className="text-right whitespace-nowrap"
                      title={looksLikeIsoDate(referenceDate.trim())
                        ? `Deployed ${formatRefDate(p.deploymentDate)}; analysis anchored on ${referenceDate.trim()}`
                        : `Deployed ${formatRefDate(p.deploymentDate)} (also analysis anchor)`
                      }
                    >{formatRefDate(p.deploymentDate)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {formatNumber(p.clicksBefore)} → {formatNumber(p.clicksAfter)}
                    </TableCell>
                    <TableCell className="text-right">{formatDelta(p.clicksDelta)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {formatNumber(p.impressionsBefore)} → {formatNumber(p.impressionsAfter)}
                    </TableCell>
                    <TableCell className="text-right">{formatDelta(p.impressionsDelta)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {formatPct(p.ctrBeforePct)} → {formatPct(p.ctrAfterPct)}
                    </TableCell>
                    <TableCell className="text-right">{formatPctDelta(p.ctrDeltaPp)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {formatPosition(p.positionBefore)} → {formatPosition(p.positionAfter)}
                    </TableCell>
                    <TableCell className="text-right">{formatPositionDelta(p.positionDelta)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
